/**
 * Session state: who is signed in, which workspace is open, where the server is. Persisted to localStorage so a
 * reload keeps the user signed in. Offline mode creates a local user and workspace with no server at all.
 */
import { create } from "zustand";
import { defaultServerUrl, SERVER_URL_EDITABLE } from "./server-url";
import { newId, nowIso, type User, type Workspace, type WorkspaceRole } from "@hub/shared";
import { api, configureApi } from "@/api/client";
import { hostMode } from "@/engine/transport";

const KEYS = { token: "hub_token", user: "hub_user", workspaceId: "hub_workspace_id", serverUrl: "hub_server_url", workspaces: "hub_workspaces", deviceId: "hub_device_id", offline: "hub_offline", onboarded: "hub_onboarded", pendingLocal: "hub_pending_local_workspace" } as const;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return key === KEYS.token || key === KEYS.workspaceId || key === KEYS.serverUrl ? (raw as unknown as T) : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch {
    // Storage can be unavailable (private mode); the session then lives in memory only.
  }
}

export type SessionWorkspace = Workspace & { role: WorkspaceRole; memberCount?: number };

export interface SessionState {
  token: string | null;
  user: User | null;
  workspaceId: string | null;
  workspaces: SessionWorkspace[];
  serverUrl: string;
  deviceId: string;
  /** Local only: no server, no sync. */
  offline: boolean;
  onboarded: boolean;
  pendingEmail: string | null;
  devCode: string | null;
  busy: boolean;
  error: string | null;
  isSignedIn: boolean;
  /** The offline workspace left behind by a sign-in; its data is adopted into the server workspace once the engine is up. */
  pendingLocalWorkspaceId: string | null;
  clearPendingLocalWorkspace(): void;

  requestCode(email: string): Promise<void>;
  verifyCode(code: string): Promise<void>;
  continueOffline(): void;
  setWorkspace(id: string): void;
  createWorkspace(name: string): Promise<SessionWorkspace>;
  refreshMe(): Promise<void>;
  setServerUrl(url: string): void;
  setOnboarded(done: boolean): void;
  signOut(): void;
}

export function platformForHost(): "darwin" | "win32" | "linux" | "web" | "extension" {
  const mode = hostMode();
  if (mode === "extension") return "extension";
  if (mode === "desktop") {
    const p = typeof navigator !== "undefined" ? navigator.platform : "";
    return /Mac/.test(p) ? "darwin" : /Win/.test(p) ? "win32" : "linux";
  }
  return "web";
}

function deviceName(): string {
  if (typeof navigator === "undefined") return "Hub";
  const ua = navigator.userAgent;
  const browser = /Chrome/.test(ua) ? "Chrome" : /Safari/.test(ua) ? "Safari" : /Firefox/.test(ua) ? "Firefox" : "Browser";
  const mode = hostMode();
  return mode === "desktop" ? "Hub desktop" : mode === "extension" ? `Hub extension (${browser})` : `Hub web (${browser})`;
}

const initialDeviceId = (() => {
  const existing = read<string | null>(KEYS.deviceId, null);
  if (existing) return existing;
  const id = newId();
  write(KEYS.deviceId, id);
  return id;
})();

// `isSignedIn` is derived from user + workspaceId. zustand merges state with Object.assign, which would freeze a getter
// into a stale boolean, so it is a plain field kept in sync by the subscription below (covers external setState too).
export const useSession = create<SessionState>((set, get) => {
  return ({
  token: read<string | null>(KEYS.token, null),
  user: read<User | null>(KEYS.user, null),
  workspaceId: read<string | null>(KEYS.workspaceId, null),
  workspaces: read<SessionWorkspace[]>(KEYS.workspaces, []),
  // A release build ignores any address saved earlier (a LAN address from the beta, say) and uses the one server.
  serverUrl: (SERVER_URL_EDITABLE ? read<string | null>(KEYS.serverUrl, null) : null) || defaultServerUrl(),
  deviceId: initialDeviceId,
  offline: read<boolean>(KEYS.offline, false),
  onboarded: read<boolean>(KEYS.onboarded, false),
  pendingEmail: null,
  devCode: null,
  pendingLocalWorkspaceId: read<string | null>(KEYS.pendingLocal, null),
  busy: false,
  error: null,
  isSignedIn: !!read<User | null>(KEYS.user, null) && !!read<string | null>(KEYS.workspaceId, null) && !!read<string | null>(KEYS.token, null) && !read<boolean>(KEYS.offline, false),

  async requestCode(email) {
    set({ busy: true, error: null });
    try {
      const res = await api.requestCode(email.trim().toLowerCase());
      set({ pendingEmail: email.trim().toLowerCase(), devCode: res.devCode ?? null, busy: false });
    } catch (e) {
      set({ busy: false, error: e instanceof Error ? e.message : "Could not reach the server" });
      throw e;
    }
  },

  async verifyCode(code) {
    const email = get().pendingEmail;
    if (!email) throw new Error("Request a code first");
    set({ busy: true, error: null });
    try {
      const res = await api.verifyCode(email, code.trim(), { name: deviceName(), platform: platformForHost() });
      const workspaces = res.workspaces;
      const prior = get();
      const workspaceId = workspaces.find((w) => w.id === prior.workspaceId)?.id ?? workspaces[0]?.id ?? null;
      // Data gathered offline, or under an account on another server, must not vanish behind the new account: remember
      // that workspace so it is adopted. Signing back in to the same workspace leaves nothing to adopt.
      // A token the server rejected leaves the workspace in place with no token; that workspace counts as left behind too.
      const left = prior.offline ? prior.workspaceId : (prior.pendingLocalWorkspaceId ?? prior.workspaceId);
      const pendingLocal = left && left !== workspaceId ? left : null;
      write(KEYS.pendingLocal, pendingLocal);
      write(KEYS.token, res.token);
      // The server does not know the device's zone; the engine uses it for reminder times.
      const user = { ...res.user, timezone: res.user.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? null };
      write(KEYS.user, user);
      write(KEYS.workspaces, workspaces);
      write(KEYS.workspaceId, workspaceId);
      write(KEYS.offline, false);
      set({ token: res.token, user, workspaces, workspaceId, offline: false, busy: false, pendingEmail: null, devCode: null, pendingLocalWorkspaceId: pendingLocal });
    } catch (e) {
      set({ busy: false, error: e instanceof Error ? e.message : "That code did not work" });
      throw e;
    }
  },

  continueOffline() {
    const existing = get();
    const user: User = existing.user ?? { id: newId(), email: "local@hub", username: "you", firstName: null, lastName: null, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null, created: nowIso(), updated: nowIso() };
    const workspace: SessionWorkspace = existing.workspaces[0] ?? { id: newId(), name: "Personal", avatarUrl: null, created: nowIso(), updated: nowIso(), role: "owner" };
    write(KEYS.user, user);
    write(KEYS.workspaces, [workspace]);
    write(KEYS.workspaceId, workspace.id);
    write(KEYS.offline, true);
    write(KEYS.token, null);
    set({ user, workspaces: [workspace], workspaceId: workspace.id, offline: true, token: null, error: null });
  },

  clearPendingLocalWorkspace() {
    write(KEYS.pendingLocal, null);
    set({ pendingLocalWorkspaceId: null });
  },

  setWorkspace(id) {
    write(KEYS.workspaceId, id);
    set({ workspaceId: id });
  },

  async createWorkspace(name) {
    if (get().offline || !get().token) {
      const ws: SessionWorkspace = { id: newId(), name, avatarUrl: null, created: nowIso(), updated: nowIso(), role: "owner" };
      const workspaces = [...get().workspaces, ws];
      write(KEYS.workspaces, workspaces);
      write(KEYS.workspaceId, ws.id);
      set({ workspaces, workspaceId: ws.id });
      return ws;
    }
    const { workspace } = await api.workspaces.create({ name });
    const ws: SessionWorkspace = { ...workspace, role: "owner" };
    const workspaces = [...get().workspaces, ws];
    write(KEYS.workspaces, workspaces);
    write(KEYS.workspaceId, ws.id);
    set({ workspaces, workspaceId: ws.id });
    return ws;
  },

  async refreshMe() {
    if (!get().token) return;
    const me = await api.me();
    write(KEYS.user, me.user);
    write(KEYS.workspaces, me.workspaces);
    set({ user: me.user, workspaces: me.workspaces, workspaceId: get().workspaceId ?? me.workspaces[0]?.id ?? null });
  },

  setServerUrl(url) {
    if (!SERVER_URL_EDITABLE) return;
    const clean = url.trim().replace(/\/$/, "") || defaultServerUrl();
    write(KEYS.serverUrl, clean);
    set({ serverUrl: clean });
  },

  setOnboarded(done) {
    write(KEYS.onboarded, done);
    set({ onboarded: done });
  },

  signOut() {
    const { token, workspaceId } = get();
    if (token) api.logout().catch(() => undefined);
    for (const k of [KEYS.token, KEYS.user, KEYS.workspaceId, KEYS.workspaces, KEYS.offline, KEYS.onboarded]) write(k, null);
    // The data stays on this device under the workspace just left; the next sign-in offers to carry it over.
    write(KEYS.pendingLocal, workspaceId);
    set({ token: null, user: null, workspaceId: null, workspaces: [], offline: false, onboarded: false, pendingEmail: null, devCode: null, error: null, pendingLocalWorkspaceId: workspaceId });
  },
});
});

configureApi({
  getToken: () => useSession.getState().token,
  getServerUrl: () => useSession.getState().serverUrl,
  onUnauthorized: () => {
    const s = useSession.getState();
    if (s.token) {
      write(KEYS.token, null);
      useSession.setState({ token: null });
    }
  },
});

/** Convenience selector used by the network feature (see CONTRACT.md). */
export function useIsSignedIn(): boolean {
  return useSession((s) => !!s.user && !!s.workspaceId);
}

useSession.subscribe((s) => {
  const v = !!s.user && !!s.workspaceId && !!s.token && !s.offline;
  if (s.isSignedIn !== v) useSession.setState({ isSignedIn: v });
});
